/**
 * Pool conditions fields
 *
 * The campaign-agnostic gift matching conditions a pool can carry. Page conditions are not offered.
 */

import React from 'react';
import { Checkbox, Form, Message } from 'semantic-ui-react';
import { I18n, Translate } from 'react-redux-i18n';
import { Controller, useFormContext } from 'react-hook-form';
import { giftMatchingKeys, i18nKeys } from '../constants';
import {
  ConditionFormValues,
  conditionOperator,
  PoolFormValues
} from '../types';
import Styles from '../GiftMatchingPools.module.css';

type ToggleName = Exclude<keyof ConditionFormValues, 'donationAmount'>;

const toggles: { name: ToggleName; labelKey: string; dateTime?: boolean }[] = [
  {
    name: 'startsAt',
    labelKey: giftMatchingKeys.CONDITIONS.START,
    dateTime: true
  },
  { name: 'endsAt', labelKey: giftMatchingKeys.CONDITIONS.END, dateTime: true },
  {
    name: 'giftMatchingTotal',
    labelKey: giftMatchingKeys.CONDITIONS.GIFT_MATCHING_TOTAL
  },
  {
    name: 'campaignTotal',
    labelKey: giftMatchingKeys.CONDITIONS.CAMPAIGN_TOTAL
  },
  {
    name: 'donationCount',
    labelKey: giftMatchingKeys.CONDITIONS.DONATION_COUNT
  }
];

const amountOperators = [
  conditionOperator.lesserOrEqualThan,
  conditionOperator.between,
  conditionOperator.greaterOrEqualThan
];

interface PoolConditionsFieldsProps {
  errorKeys: string[];
  idPrefix: string;
}

const PoolConditionsFields: React.FC<PoolConditionsFieldsProps> = ({
  errorKeys,
  idPrefix
}) => {
  const { control, register, watch } = useFormContext<PoolFormValues>();
  const conditions = watch('conditions');

  return (
    <fieldset className={Styles.fieldset}>
      <legend className={Styles.legend}>
        <Translate value={i18nKeys.FIELDS.CONDITIONS_TITLE} />
      </legend>
      <p className="field-description">
        <Translate value={i18nKeys.FIELDS.CONDITIONS_HELP} />
      </p>
      <p className="field-description">
        <Translate value={i18nKeys.FIELDS.PAGE_CONDITIONS_NOTE} />
      </p>

      {toggles.map(({ name, labelKey, dateTime }) => {
        const inputId = `${idPrefix}-condition-${name}`;
        return (
          <div key={name} className={Styles.condition}>
            <Form.Field>
              <Controller
                name={`conditions.${name}.enabled`}
                control={control}
                render={({ field }) => (
                  <Checkbox
                    id={`${inputId}-enabled`}
                    label={I18n.t(labelKey)}
                    checked={!!field.value}
                    onChange={(_e, data) => field.onChange(!!data.checked)}
                  />
                )}
              />
            </Form.Field>
            {conditions?.[name]?.enabled && (
              <Form.Field className={Styles.conditionValue}>
                <label htmlFor={inputId}>
                  <Translate
                    value={
                      dateTime
                        ? i18nKeys.FIELDS.DATE_TIME
                        : i18nKeys.FIELDS.VALUE
                    }
                  />
                </label>
                <input
                  id={inputId}
                  type={dateTime ? 'datetime-local' : 'number'}
                  min={dateTime ? undefined : 0}
                  step={dateTime ? undefined : 1}
                  {...register(`conditions.${name}.value`)}
                />
              </Form.Field>
            )}
          </div>
        );
      })}

      <div className={Styles.condition}>
        <Form.Field>
          <Controller
            name="conditions.donationAmount.enabled"
            control={control}
            render={({ field }) => (
              <Checkbox
                id={`${idPrefix}-condition-donationAmount-enabled`}
                label={I18n.t(giftMatchingKeys.CONDITIONS.DONATION_AMOUNT)}
                checked={!!field.value}
                onChange={(_e, data) => field.onChange(!!data.checked)}
              />
            )}
          />
        </Form.Field>
        {conditions?.donationAmount?.enabled && (
          <div className={Styles.conditionValue}>
            <Controller
              name="conditions.donationAmount.operator"
              control={control}
              render={({ field }) => (
                <Form.Group grouped>
                  {amountOperators.map((operator) => (
                    <Form.Field key={operator}>
                      <Checkbox
                        radio
                        id={`${idPrefix}-condition-donationAmount-${operator}`}
                        name={`${idPrefix}-donationAmount-operator`}
                        label={I18n.t(
                          `${giftMatchingKeys.CONDITIONS.DONATION_AMOUNT_OPERATOR}.${operator}.label`
                        )}
                        checked={field.value === operator}
                        onChange={() => field.onChange(operator)}
                      />
                    </Form.Field>
                  ))}
                </Form.Group>
              )}
            />
            <Form.Group widths="equal">
              <Form.Field>
                <label htmlFor={`${idPrefix}-condition-donationAmount-from`}>
                  <Translate
                    value={
                      conditions.donationAmount.operator ===
                      conditionOperator.between
                        ? i18nKeys.FIELDS.FROM
                        : i18nKeys.FIELDS.VALUE
                    }
                  />
                </label>
                <input
                  id={`${idPrefix}-condition-donationAmount-from`}
                  type="number"
                  min={0}
                  step={1}
                  {...register('conditions.donationAmount.from')}
                />
              </Form.Field>
              {conditions.donationAmount.operator ===
                conditionOperator.between && (
                <Form.Field>
                  <label htmlFor={`${idPrefix}-condition-donationAmount-to`}>
                    <Translate value={i18nKeys.FIELDS.TO} />
                  </label>
                  <input
                    id={`${idPrefix}-condition-donationAmount-to`}
                    type="number"
                    min={0}
                    step={1}
                    {...register('conditions.donationAmount.to')}
                  />
                </Form.Field>
              )}
            </Form.Group>
          </div>
        )}
      </div>

      {errorKeys.length > 0 && (
        <Message negative role="alert">
          {errorKeys.map((key) => (
            <p key={key}>{I18n.t(key)}</p>
          ))}
        </Message>
      )}
    </fieldset>
  );
};

export default PoolConditionsFields;
